import { useCallback, useEffect, useState } from 'react';

import API from 'my-core/api';
import { CLASS_REP_GB_DISCOUNT_PERCENT } from 'my-core/class-rep-course-utils';
import { REFERRAL_DISCOUNT_AMOUNT } from 'my-core/constants';
import { calculateDiscount, getDiscountCodeCheckoutLabel } from 'my-core/discount-code-utils';
import { isStudySessionUserRegistered, STUDY_SESSION_DEFAULT_DISCOUNT_CODE } from 'my-core/study-session-utils';
import { getSubscriptionProductDiscountPercent, useCurrentUserSubscription } from 'my-core/subscription-utils';

import CheckoutLineItems from 'my-components/Checkout/CheckoutLineItems';
import DiscountCodeInput from 'my-components/DiscountCodeInput';
import PaymentForm from 'my-components/PaymentForm';

import Button from '@mui/material/Button';

export default function StudySessionCheckoutPayment({
  currentUser,
  loading,
  noDialog,
  onClose,
  onPaymentSuccess,
  pricingInfo,
  studySession,
  studySessionUser,
}) {
  const { countryCode, currency, salesTax } = pricingInfo;

  const regPrice = studySession.regular_price || 0;

  let price = studySession.price;
  const earlyBirdDiscount = Math.max(regPrice - price, 0);
  const origPrice = earlyBirdDiscount ? regPrice : price;

  const subscription = useCurrentUserSubscription();
  const classRepDiscountPercent = currentUser.has_class_rep_discount && CLASS_REP_GB_DISCOUNT_PERCENT;
  const subscriptionDiscountPercent = !classRepDiscountPercent && getSubscriptionProductDiscountPercent(subscription);
  const defaultDiscountCodeQuery = STUDY_SESSION_DEFAULT_DISCOUNT_CODE?.code;
  const [discountCodeVal, setDiscountCodeVal] = useState(null);
  const discountCode = !subscriptionDiscountPercent && !classRepDiscountPercent && discountCodeVal;

  let subDiscount, classRepDiscount, discountCodeDiscount;
  if (classRepDiscountPercent) {
    classRepDiscount = (price * classRepDiscountPercent) / 100;
    price -= classRepDiscount;
  } else if (subscriptionDiscountPercent) {
    subDiscount = (price * subscriptionDiscountPercent) / 100;
    price -= subDiscount;
  } else {
    discountCodeDiscount = calculateDiscount(discountCode, price);
    price -= discountCodeDiscount;
  }

  const referralDiscount = currentUser.is_referred ? Math.min(REFERRAL_DISCOUNT_AMOUNT, price) : 0;
  price -= referralDiscount;

  const creditedAmount = Math.min(price, currentUser.course_credits || 0);
  price -= creditedAmount;

  const isDiscounted = !!(
    earlyBirdDiscount ||
    subDiscount ||
    classRepDiscount ||
    discountCodeDiscount ||
    referralDiscount ||
    creditedAmount
  );
  const taxAmount = Math.round(price * salesTax) / 100;
  const total = Math.round((price + taxAmount) * 100) / 100;
  const paymentDetails = {
    lineItems: [
      { label: studySession.title, amount: origPrice, highlight: !isDiscounted },
      earlyBirdDiscount && { label: 'Early Bird Discount', amount: -earlyBirdDiscount },
      subDiscount && {
        label: 'Wizeprep Plus Member',
        caption: `Additional ${subscriptionDiscountPercent}% Discount`,
        amount: -subDiscount,
      },
      classRepDiscount && { label: `Class Rep ${classRepDiscountPercent}% Discount`, amount: -classRepDiscount },
      discountCodeDiscount && {
        label: getDiscountCodeCheckoutLabel(discountCode),
        amount: -discountCodeDiscount,
      },
      referralDiscount && { label: 'Refer a Friend Discount', amount: -referralDiscount },
      creditedAmount && { label: 'Credits', amount: -creditedAmount },
      isDiscounted && { label: 'Discounted Subtotal', amount: price, highlight: true },
      taxAmount && { label: `Sales Tax (${salesTax}%)`, amount: taxAmount },
      { label: 'Total', amount: total },
    ].filter(Boolean),
    total,
    tax: taxAmount,
    currency,
  };

  const [purchaseIntentRequest, setPurchaseIntentRequest] = useState();
  const purchaseIntent = purchaseIntentRequest?.intent;
  const piRequestKey = `ss_${studySession.id}_dc_${discountCode?.id}`;
  useEffect(() => {
    if (studySession.id && !!total && !isStudySessionUserRegistered(studySessionUser)) {
      setPurchaseIntentRequest();
      API.postJSON(`/api/v1/study_sessions/${studySession.id}/users/create_purchase_intent`, {
        discount_code_id: discountCode?.id,
        course_id: studySession.course?.id,
      })
        .then(intent => {
          setPurchaseIntentRequest({ intent, key: piRequestKey });
        })
        .catch(error => {
          setPurchaseIntentRequest({ error, key: piRequestKey });
        });
    }
  }, [studySession.id, studySession.course?.id, discountCode?.id, total, studySessionUser, piRequestKey]);

  const priceMatch = Math.round(total * 100) === purchaseIntent?.amount;

  const handlePaymentSuccess = useCallback(
    ({ paymentIntent }) => onPaymentSuccess({ paymentIntent, total, discountCode, currency }),
    [onPaymentSuccess, total, discountCode, currency],
  );

  return (
    <>
      {!subscriptionDiscountPercent && !classRepDiscountPercent && (
        <DiscountCodeInput
          ButtonProps={{ variant: 'outlined' }}
          defaultQuery={defaultDiscountCodeQuery || discountCode?.code}
          onVerified={setDiscountCodeVal}
          productId={studySession.id}
          productType="StudySession"
          TextFieldProps={{ placeholder: 'Discount code' }}
          verifyDefaultQuery={!!defaultDiscountCodeQuery}
        />
      )}
      <CheckoutLineItems css={({ spacing }) => ({ margin: spacing(2, 0) })} paymentDetails={paymentDetails} />
      {total ?
        <PaymentForm
          amount={purchaseIntent?.amount || false}
          countryCode={countryCode}
          currency={currency}
          disabled={loading || !priceMatch}
          error={
            purchaseIntentRequest?.error ||
            (purchaseIntentRequest?.key === piRequestKey &&
              !priceMatch &&
              'There was an error preparing for your payment. Please refresh the page. If this issue persists, please contact support.')
          }
          onCancel={noDialog && onClose}
          onSuccess={handlePaymentSuccess}
          paymentIntent={purchaseIntent}
          paymentMethod={purchaseIntent?.payment_method}
          submitting={loading}
        />
      : <div css={{ textAlign: 'right' }}>
          <Button onClick={() => onPaymentSuccess({ total, discountCode, currency })} variant="contained">
            Register
          </Button>
        </div>
      }
    </>
  );
}
